/**
 * Modo PREVIEW POR LINK: la app del cliente se abre con
 * `?veoPreview=<token>` (link generado desde el dashboard) y el SDK canjea
 * el token contra `POST /v1/guides/preview-resolve` para renderizar ESA
 * guía como vista previa — aunque esté en draft — sobre la app real.
 *
 * A diferencia del modo builder no hay puente postMessage ni dashboard
 * abierto: es un link compartible para que el equipo pruebe la guía sin
 * publicarla. Reusa `previewGuide` (sin tracking/frecuencia/resolver) y
 * monta un chip flotante con "volver a mostrar" y "cerrar".
 *
 * Vive en el bundle de guías (`veo-guides.js`), NO en el `veo.js` core —
 * el core solo detecta el query param y dispara la carga del bundle.
 */
import { clearPreviewToken } from '../../core/preview-loader';
import { hasDocument } from '../../utils/safe-env';
import { previewGuide } from './guide-preview';
import type { CompositeStep, ResolvedGuide } from './types';
import { stepMatchesPage } from './url-match';

export interface PreviewModeOptions {
  apiKey: string;
  apiUrl: string;
  /** Token firmado del link (`?veoPreview=<token>`). */
  token: string;
  debug?: boolean;
}

export interface PreviewModeHandle {
  /** Cierra la preview y quita el chip. Idempotente. */
  teardown(): void;
}

let active: PreviewModeHandle | null = null;

/**
 * Canjea el token y renderiza la guía como vista previa + chip de control.
 * Si el token es inválido/expirado, el chip lo informa (solo quien tiene el
 * link lo ve). Solo una sesión de preview activa a la vez.
 */
export async function runPreviewMode(opts: PreviewModeOptions): Promise<PreviewModeHandle | null> {
  if (!hasDocument()) return null;
  active?.teardown();

  let guide: ResolvedGuide | null = null;
  try {
    const res = await fetch(`${opts.apiUrl}/v1/guides/preview-resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': opts.apiKey },
      body: JSON.stringify({ token: opts.token }),
    });
    if (res.ok) {
      const data = (await res.json()) as { guide?: ResolvedGuide };
      guide = data.guide ?? null;
    } else if (opts.debug) {
      console.warn('[veo] preview-resolve respondió', res.status);
    }
  } catch (err) {
    if (opts.debug) console.warn('[veo] preview-resolve falló:', err);
  }

  /**
   * En el link de preview el envío de forms es REAL: corre sobre la app del
   * cliente con el SDK inicializado, así que la respuesta se guarda en el
   * usuario que está probando (su propia identidad) — permite verificar el
   * flujo completo props→segmentos sin tocar a nadie más.
   */
  const submitToBackend = async (answers: Record<string, unknown>): Promise<boolean> => {
    if (!guide) return false;
    const veo = (
      window as unknown as {
        veo?: { getEndUserId?: () => string | null; getAnonymousId?: () => string };
      }
    ).veo;
    const endUserId = veo?.getEndUserId?.() ?? veo?.getAnonymousId?.() ?? null;
    if (!endUserId) return false;
    try {
      const res = await fetch(`${opts.apiUrl}/v1/guides/${guide.guideId}/form-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': opts.apiKey },
        body: JSON.stringify({ endUserId, answers }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  let preview: { close(): void } | null = null;
  let delayTimer: ReturnType<typeof setTimeout> | null = null;
  let disarmTrigger: (() => void) | null = null;
  const baseLabel = guide ? `Vista previa: ${guide.guideName}` : '';

  const clearWaits = (): void => {
    if (delayTimer) clearTimeout(delayTimer);
    delayTimer = null;
    disarmTrigger?.();
    disarmTrigger = null;
  };

  /** Pinta la guía (composite: el PRIMER paso que aplica a esta URL, como el runtime). */
  const paint = (): void => {
    if (!guide) return;
    preview?.close();
    let startStepIndex: number | undefined;
    if (guide.guideType === 'composite') {
      const steps = guide.guideSteps as unknown as CompositeStep[];
      const idx = steps.findIndex((st) => stepMatchesPage(st.pageLocation, window.location.href));
      if (idx === -1) {
        chip.setLabel(`${baseLabel} · ningún paso aplica a esta página`);
        return;
      }
      startStepIndex = idx;
    }
    chip.setLabel(baseLabel);
    preview = previewGuide({
      guideType: guide.guideType,
      guideSteps: guide.guideSteps,
      activationRules: guide.activationRules,
      guideName: guide.guideName,
      ...(startStepIndex !== undefined ? { startStepIndex } : {}),
      formSubmit: submitToBackend,
      formSubmitNote: 'Vista previa: guardado en TU usuario para probar el flujo.',
    });
  };

  /**
   * Muestra la guía como la vería el usuario: respeta «retrasar la aparición»
   * y el disparo por elemento objetivo (clic/hover). El chip explica la espera;
   * ↻ vuelve a pasar por el mismo camino.
   */
  const show = (): void => {
    if (!guide) return;
    clearWaits();
    const rules = guide.activationRules;
    const selector = rules.selector?.trim();
    if ((rules.trigger === 'on_click' || rules.trigger === 'on_hover') && selector) {
      const verb = rules.trigger === 'on_hover' ? 'pasa el mouse por' : 'haz clic en';
      chip.setLabel(`${baseLabel} · ${verb} el elemento objetivo`);
      const onFire = (e: Event): void => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        if (target.closest('[data-veo-preview-chip],[data-veo-guide]')) return;
        try {
          if (!target.closest(selector)) return;
        } catch {
          return;
        }
        clearWaits();
        paint();
      };
      document.addEventListener('click', onFire, true);
      if (rules.trigger === 'on_hover') document.addEventListener('mouseover', onFire, true);
      disarmTrigger = () => {
        document.removeEventListener('click', onFire, true);
        document.removeEventListener('mouseover', onFire, true);
      };
      return;
    }
    const delayMs = rules.trigger === 'delayed' ? (rules.delayMs ?? 0) : 0;
    if (delayMs > 0) {
      chip.setLabel(`${baseLabel} · aparece en ${Math.round(delayMs / 1000)} s`);
      delayTimer = setTimeout(() => {
        delayTimer = null;
        paint();
      }, delayMs);
      return;
    }
    paint();
  };

  const chip = mountChip({
    label: guide ? baseLabel : 'Link de preview inválido o vencido',
    error: !guide,
    onReshow: guide ? show : null,
    onClose: () => handle.teardown(),
  });

  const handle: PreviewModeHandle = {
    teardown() {
      clearWaits();
      preview?.close();
      preview = null;
      chip.remove();
      // Salir de verdad: borra el token persistido para que NO reaparezca al
      // navegar (si no, sessionStorage lo reviviría en la próxima página).
      clearPreviewToken();
      if (active === handle) active = null;
    },
  };
  active = handle;
  show();
  return handle;
}

interface ChipOptions {
  label: string;
  error: boolean;
  onReshow: (() => void) | null;
  onClose: () => void;
}

/** Chip flotante (Shadow DOM, abajo-derecha) con re-mostrar y cerrar. */
function mountChip(opts: ChipOptions): { remove(): void; setLabel(text: string): void } {
  const host = document.createElement('div');
  host.setAttribute('data-veo-preview-chip', '');
  const root = host.attachShadow({ mode: 'open' });

  const style = document.createElement('style');
  style.textContent = `
    .chip { position: fixed; bottom: 16px; right: 16px; z-index: 2147483647;
      display: flex; align-items: center; gap: 8px; padding: 8px 12px;
      background: #111827; color: #f9fafb; border-radius: 9999px;
      font: 500 12px/1.2 system-ui, sans-serif; box-shadow: 0 4px 12px rgba(0,0,0,.25); }
    .chip.error { background: #7f1d1d; }
    .label { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    button { all: unset; cursor: pointer; padding: 2px 6px; border-radius: 9999px;
      font-size: 12px; line-height: 1; }
    button:hover { background: rgba(255,255,255,.15); }
  `;
  root.appendChild(style);

  const chip = document.createElement('div');
  chip.className = opts.error ? 'chip error' : 'chip';

  const label = document.createElement('span');
  label.className = 'label';
  label.textContent = opts.label;
  chip.appendChild(label);

  if (opts.onReshow) {
    const reshow = document.createElement('button');
    reshow.textContent = '↻';
    reshow.title = 'Volver a mostrar la guía';
    reshow.addEventListener('click', opts.onReshow);
    chip.appendChild(reshow);
  }

  const close = document.createElement('button');
  close.textContent = '✕';
  close.title = 'Salir de la vista previa';
  close.addEventListener('click', opts.onClose);
  chip.appendChild(close);

  root.appendChild(chip);
  document.body.appendChild(host);
  return {
    remove: () => host.remove(),
    setLabel: (text) => {
      label.textContent = text;
      label.title = text;
    },
  };
}
